/**
 * Aceternity-style multi-step loader.
 *
 * Auto-progresses through a list of `steps` on a fixed interval. Each step
 * has three visual states: pending (dim, empty circle), loading (current —
 * bright, animated spinner), done (checkmark, faded text).
 *
 * If you have an external signal that the work is finished (e.g. analysis
 * status flips from "processing" to "completed"), set `loading={false}` to
 * jump every step to "done".
 */

import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import './multi-step-loader.css';

interface MultiStepLoaderProps {
  steps: string[];
  /** Whether the work is still in progress. When false, every step is done. */
  loading: boolean;
  /** Time spent on each step in ms. Default 4000. */
  stepDurationMs?: number;
}

export function MultiStepLoader({
  steps,
  loading,
  stepDurationMs = 4000,
}: MultiStepLoaderProps) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!loading) {
      setCurrentStep(steps.length); // mark all done
      return;
    }
    setCurrentStep(0);
    const id = window.setInterval(() => {
      setCurrentStep((prev) => {
        // Hold on the last step until external `loading` flips
        if (prev >= steps.length - 1) return prev;
        return prev + 1;
      });
    }, stepDurationMs);
    return () => window.clearInterval(id);
  }, [loading, steps.length, stepDurationMs]);

  return (
    <ul className="msl-list" aria-live="polite">
      {steps.map((label, i) => {
        const state =
          !loading || i < currentStep ? 'done' :
          i === currentStep ? 'loading' :
          'pending';
        return (
          <li key={i} className={`msl-step msl-step-${state}`}>
            <span className="msl-icon" aria-hidden="true">
              {state === 'done' ? (
                <Check size={14} strokeWidth={2.5} />
              ) : state === 'loading' ? (
                <Loader2 size={14} strokeWidth={2.5} className="msl-spin" />
              ) : (
                <span className="msl-dot" />
              )}
            </span>
            <span className="msl-label">{label}</span>
          </li>
        );
      })}
    </ul>
  );
}
